import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, type WritableSignal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, map, of, switchMap, type Observable } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { NgpSelectPortal } from 'ng-primitives/select';
import {
  heroArrowDownTray,
  heroArrowLeft,
  heroArrowPath,
  heroArrowUpTray,
  heroBookOpen,
  heroCheck,
  heroCheckCircle,
  heroChevronDown,
  heroChevronRight,
  heroChatBubbleLeftRight,
  heroCircleStack,
  heroClock,
  heroCloudArrowUp,
  heroDocumentArrowUp,
  heroDocumentDuplicate,
  heroDocumentPlus,
  heroDocumentText,
  heroEllipsisVertical,
  heroFolderOpen,
  heroLink,
  heroListBullet,
  heroPencilSquare,
  heroPlus,
  heroQueueList,
  heroRectangleGroup,
  heroShieldCheck,
  heroShoppingBag,
  heroSparkles,
  heroSquares2x2,
  heroTableCells,
  heroTrash,
  heroXCircle,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import type { CreateKnowledgeBaseRequest } from '../../core/api/generated/models/CreateKnowledgeBaseRequest';
import type { CreateKnowledgeSourceRequest } from '../../core/api/generated/models/CreateKnowledgeSourceRequest';
import type { KnowledgeBase } from '../../core/api/generated/models/KnowledgeBase';
import type { KnowledgeBaseStatus } from '../../core/api/generated/models/KnowledgeBaseStatus';
import type { KnowledgeBaseType } from '../../core/api/generated/models/KnowledgeBaseType';
import type { KnowledgeDocument } from '../../core/api/generated/models/KnowledgeDocument';
import type { KnowledgeIngestionJob } from '../../core/api/generated/models/KnowledgeIngestionJob';
import type { KnowledgeRetrievalResult } from '../../core/api/generated/models/KnowledgeRetrievalResult';
import type { KnowledgeSource } from '../../core/api/generated/models/KnowledgeSource';
import type { UpdateKnowledgeSourceRequest } from '../../core/api/generated/models/UpdateKnowledgeSourceRequest';
import { AuthStore } from '../../core/auth/auth.store';
import {
  ToastrService,
  UiBadge,
  UiBulkActionBar,
  UiBulkActionBarCount,
  UiBulkActionBarGroup,
  UiBulkActionBarSeparator,
  UiBulkActionButton,
  UiButton,
  UiCheckbox,
  UiConfirmDialogService,
  UiDataTable,
  UiDataTableCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiDialog,
  UiDialogDescription,
  UiDialogOverlay,
  UiDialogTitle,
  UiEmptyState,
  UiFilePicker,
  UiHint,
  UiInput,
  UiLabel,
  UiMenuItem,
  UiMenuPanel,
  UiMenuSeparator,
  UiOptionRow,
  UiOptionRowCopy,
  UiOptionRowIcon,
  UiPageHeader,
  UiPanelCard,
  UiPulseDot,
  UiRecordRow,
  UiRecordRowButton,
  UiResizableColumns,
  UiResourceCard,
  UiResourceCardBody,
  UiResourceCardDescription,
  UiResourceCardEyebrow,
  UiResourceCardFooter,
  UiResourceCardHeader,
  UiResourceCardTitle,
  UiResourceGrid,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiSkeletonRow,
  UiSpinner,
  UiIconToggleGroup,
  UiIconToggleGroupItem,
  UiIconToggleIndicator,
  UiSwitch,
  UiSwitchThumb,
  UiTextarea,
  UiToggleGroup,
  UiToggleGroupItem,
} from '../../shared/ui';
import { HeaderSearchService } from '../../core/search/header-search.service';
import { KnowledgeApiService } from './knowledge-api.service';
import { KnowledgeFileUploadService } from './knowledge-file-upload.service';

type LibraryViewMode = 'grid' | 'list';
type SourceComposerMode = 'url' | 'file' | 'manual' | 'csv';
type SourceUpdateMode = 'metadata' | 'file' | 'manual' | 'csv';
type UpdateContext = 'source' | 'document';
type DocumentSectionKind = 'text' | 'list' | 'metadata';
type DocumentStatusFilter = 'all' | 'active' | 'inactive' | 'deleted';
type SourceProcessingState = 'success' | 'failed' | 'processing' | 'pending';
type KnowledgeBadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';

type DocumentSection = {
  key: string;
  label: string;
  value: string;
  items: readonly string[];
  kind: DocumentSectionKind;
  display: 'text' | 'list' | 'badges';
};

type EditableDocumentSection = {
  key: string;
  label: string;
  value: string;
  kind: DocumentSectionKind;
};

type DocumentViewModel = {
  document: KnowledgeDocument;
  title: string;
  preview: string;
  sourceName: string;
  sections: readonly DocumentSection[];
};

type DocumentStatusFilterOption = {
  value: DocumentStatusFilter;
  label: string;
};

type KnowledgeBaseTypeOption = {
  value: KnowledgeBaseType;
  label: string;
  icon: string;
  description: string;
};

const BASE_TYPE_OPTIONS: readonly KnowledgeBaseTypeOption[] = [
  {
    value: 'faq',
    label: 'FAQ',
    icon: 'heroBookOpen',
    description: 'Perguntas frequentes com respostas objetivas.',
  },
  {
    value: 'processes',
    label: 'Processos',
    icon: 'heroCircleStack',
    description: 'Etapas, pré-requisitos e exceções operacionais.',
  },
  {
    value: 'policies',
    label: 'Políticas',
    icon: 'heroShieldCheck',
    description: 'Regras internas, escopo e condições de aplicação.',
  },
  {
    value: 'sales_scripts',
    label: 'Scripts de vendas',
    icon: 'heroChatBubbleLeftRight',
    description: 'Abordagens, objeções e critérios comerciais.',
  },
  {
    value: 'product_catalog',
    label: 'Catálogo de produtos',
    icon: 'heroShoppingBag',
    description: 'Produtos, SKUs, especificações e informações comerciais.',
  },
  {
    value: 'playbooks',
    label: 'Playbooks',
    icon: 'heroSparkles',
    description: 'Guias de execução, critérios e boas práticas.',
  },
  {
    value: 'other',
    label: 'Outro',
    icon: 'heroSquares2x2',
    description: 'Conteúdo geral para recuperação pelos agentes.',
  },
];

const TYPE_LABELS = Object.fromEntries(BASE_TYPE_OPTIONS.map((option) => [option.value, option.label])) as Record<
  KnowledgeBaseType,
  string
>;

const CSV_TEMPLATES: Record<KnowledgeBaseType, readonly string[]> = {
  faq: ['question', 'answer', 'category', 'related_topics'],
  processes: ['process_name', 'steps', 'prerequisites', 'expected_outcome', 'exceptions'],
  policies: ['policy_name', 'scope', 'rules', 'effective_date', 'exceptions'],
  sales_scripts: ['scenario', 'opening', 'discovery_questions', 'objection_handling', 'closing'],
  product_catalog: ['name', 'sku', 'price', 'description', 'category', 'features', 'specs'],
  playbooks: ['playbook_name', 'objective', 'steps', 'success_criteria', 'tips'],
  other: ['title', 'content', 'tags', 'metadata'],
};

const DOCUMENT_STATUS_FILTERS: readonly DocumentStatusFilterOption[] = [
  { value: 'all', label: 'Todos os status' },
  { value: 'active', label: 'Gerados' },
  { value: 'inactive', label: 'Inativos' },
  { value: 'deleted', label: 'Excluídos' },
];

@Component({
  selector: 'app-knowledge-page',
  imports: [
    NgIcon,
    NgpMenuTrigger,
    NgpSelectPortal,
    UiBadge,
    UiBulkActionBar,
    UiBulkActionBarCount,
    UiBulkActionBarGroup,
    UiBulkActionBarSeparator,
    UiBulkActionButton,
    UiButton,
    UiCheckbox,
    UiDataTable,
    UiDataTableCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiDialog,
    UiDialogDescription,
    UiDialogOverlay,
    UiDialogTitle,
    UiEmptyState,
    UiFilePicker,
    UiHint,
    UiInput,
    UiLabel,
    UiMenuItem,
    UiMenuPanel,
    UiMenuSeparator,
    UiOptionRow,
    UiOptionRowCopy,
    UiOptionRowIcon,
    UiPageHeader,
    UiPanelCard,
    UiPulseDot,
    UiRecordRow,
    UiRecordRowButton,
    UiResizableColumns,
    UiResourceCard,
    UiResourceCardBody,
    UiResourceCardDescription,
    UiResourceCardEyebrow,
    UiResourceCardFooter,
    UiResourceCardHeader,
    UiResourceCardTitle,
    UiResourceGrid,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiSkeletonRow,
    UiSpinner,
    UiIconToggleGroup,
    UiIconToggleGroupItem,
    UiIconToggleIndicator,
    UiSwitch,
    UiSwitchThumb,
    UiTextarea,
    UiToggleGroup,
    UiToggleGroupItem,
  ],
  providers: [
    provideIcons({
      heroArrowDownTray,
      heroArrowLeft,
      heroArrowPath,
      heroArrowUpTray,
      heroBookOpen,
      heroCheck,
      heroCheckCircle,
      heroChevronDown,
      heroChevronRight,
      heroChatBubbleLeftRight,
      heroCircleStack,
      heroClock,
      heroCloudArrowUp,
      heroDocumentArrowUp,
      heroDocumentDuplicate,
      heroDocumentPlus,
      heroDocumentText,
      heroEllipsisVertical,
      heroFolderOpen,
      heroLink,
      heroListBullet,
      heroPencilSquare,
      heroPlus,
      heroQueueList,
      heroRectangleGroup,
      heroShieldCheck,
      heroShoppingBag,
      heroSparkles,
      heroSquares2x2,
      heroTableCells,
      heroTrash,
      heroXCircle,
      heroXMark,
    }),
  ],
  templateUrl: './knowledge.page.html',
  styleUrl: './knowledge.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KnowledgePage {
  private readonly api = inject(KnowledgeApiService);
  private readonly fileUpload = inject(KnowledgeFileUploadService);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly headerSearch = inject(HeaderSearchService);
  private readonly toasts = inject(ToastrService);
  private readonly confirm = inject(UiConfirmDialogService);

  readonly typeOptions = BASE_TYPE_OPTIONS;
  readonly documentStatusFilters = DOCUMENT_STATUS_FILTERS;

  readonly libraryView = signal<LibraryViewMode>('grid');
  readonly selectedBaseId = signal<string | null>(null);
  readonly loadingBases = signal(false);
  readonly loadingDetail = signal(false);
  readonly creatingBase = signal(false);
  readonly sourceDialogOpen = signal(false);
  readonly savingBase = signal(false);
  readonly archivingBaseIds = signal<string[]>([]);
  readonly savingSource = signal(false);
  readonly updatingSource = signal(false);
  readonly retrieving = signal(false);
  readonly accessBlocked = signal(false);

  readonly bases = signal<KnowledgeBase[]>([]);
  readonly sources = signal<KnowledgeSource[]>([]);
  readonly documents = signal<KnowledgeDocument[]>([]);
  readonly jobs = signal<KnowledgeIngestionJob[]>([]);
  readonly retrievalResults = signal<KnowledgeRetrievalResult[]>([]);

  readonly baseName = signal('');
  readonly baseDescription = signal('');
  readonly baseType = signal<KnowledgeBaseType>('faq');

  readonly sourceMode = signal<SourceComposerMode>('url');
  readonly sourceName = signal('');
  readonly sourceUrl = signal('');
  readonly sourceTags = signal('');
  readonly sourceManualContent = signal('');
  readonly sourceFile = signal<File | null>(null);
  readonly sourceCsvFile = signal<File | null>(null);

  readonly editingSourceId = signal<string | null>(null);
  readonly editingDocumentId = signal<string | null>(null);
  readonly updateContext = signal<UpdateContext>('source');
  readonly sourceUpdateMode = signal<SourceUpdateMode>('metadata');
  readonly updateSourceName = signal('');
  readonly updateSourceUrl = signal('');
  readonly updateSourceTags = signal('');
  readonly updateManualContent = signal('');
  readonly updateFile = signal<File | null>(null);
  readonly updateCsvFile = signal<File | null>(null);
  readonly expandedSourceId = signal<string | null>(null);

  readonly editingDocumentContentId = signal<string | null>(null);
  readonly documentEditTitle = signal('');
  readonly documentEditSections = signal<EditableDocumentSection[]>([]);
  readonly documentSearch = signal('');
  readonly documentStatusFilter = signal<DocumentStatusFilter>('all');
  readonly documentSourceFilter = signal('all');
  readonly selectedDocumentIds = signal<string[]>([]);
  readonly documentMenuId = signal<string | null>(null);
  readonly duplicateDialogDocumentIds = signal<string[]>([]);
  readonly duplicateTargetBaseId = signal('');

  readonly retrievalQuery = signal('');

  readonly businessId = computed(() => this.authStore.business()?.id ?? null);
  readonly selectedBase = computed(() => this.bases().find((base) => base.id === this.selectedBaseId()) ?? null);
  readonly selectedBaseTypeOption = computed(() => this.typeOptions.find((option) => option.value === this.selectedBase()?.type) ?? null);
  readonly activeBases = computed(() => this.bases().filter((base) => base.status === 'active'));
  readonly selectedSources = computed(() => this.sources());
  readonly selectedDocuments = computed(() => this.documents());
  readonly selectedJobs = computed(() => this.jobs());
  readonly sourceById = computed(() => new Map(this.sources().map((source) => [source.id, source])));
  readonly jobsBySourceId = computed(() => {
    const map = new Map<string, KnowledgeIngestionJob[]>();
    for (const job of this.jobs()) {
      const jobs = map.get(job.sourceId) ?? [];
      jobs.push(job);
      map.set(job.sourceId, jobs);
    }
    return map;
  });
  readonly libraryViewValue = computed(() => [this.libraryView()]);
  readonly sourceModeValue = computed(() => [this.sourceMode()]);
  readonly sourceUpdateModeValue = computed(() => [this.sourceUpdateMode()]);
  readonly editingSource = computed(() => {
    const sourceId = this.editingSourceId();
    return sourceId ? this.sourceById().get(sourceId) ?? null : null;
  });
  readonly editingDocument = computed(() => this.documents().find((document) => document.id === this.editingDocumentId()) ?? null);
  readonly editingDocumentContent = computed(() => {
    const documentId = this.editingDocumentContentId();
    return documentId ? this.documents().find((document) => document.id === documentId) ?? null : null;
  });
  readonly selectedDocumentViews = computed(() =>
    this.selectedDocuments().map((document) => this.toDocumentView(document)),
  );
  readonly filteredDocumentViews = computed(() => {
    const query = normalizeSearch(this.documentSearch());
    const status = this.documentStatusFilter();
    const sourceId = this.documentSourceFilter();

    return this.selectedDocumentViews().filter((view) => {
      const matchesQuery = !query || normalizeSearch([
        view.title,
        view.preview,
        view.sourceName,
        view.document.contentText,
        ...view.sections.map((section) => section.kind === 'text' ? section.value : section.items.join(' ')),
      ].join(' ')).includes(query);
      const matchesStatus = status === 'all' || view.document.status === status;
      const matchesSource = sourceId === 'all' || view.document.sourceId === sourceId;
      return matchesQuery && matchesStatus && matchesSource;
    });
  });
  readonly documentSourceOptions = computed(() => [
    { value: 'all', label: 'Todas as fontes' },
    ...this.selectedSources().map((source) => ({ value: source.id, label: source.name })),
  ]);
  readonly documentStatusFilterLabel = computed(() =>
    this.documentStatusFilters.find((option) => option.value === this.documentStatusFilter())?.label ?? 'Todos os status',
  );
  readonly documentSourceFilterLabel = computed(() =>
    this.documentSourceOptions().find((option) => option.value === this.documentSourceFilter())?.label ?? 'Todas as fontes',
  );
  readonly selectedDocumentCount = computed(() => this.selectedDocumentIds().length);
  readonly selectedDocumentSet = computed(() => new Set(this.selectedDocumentIds()));
  readonly selectedBulkDocuments = computed(() => {
    const selectedIds = this.selectedDocumentSet();
    return this.selectedDocuments().filter((document) => selectedIds.has(document.id));
  });
  readonly duplicateDialogOpen = computed(() => this.duplicateDialogDocumentIds().length > 0);
  readonly duplicateTargetBaseName = computed(() => {
    const targetId = this.duplicateTargetBaseId();
    return this.bases().find((base) => base.id === targetId)?.name ?? 'Selecione a biblioteca';
  });
  readonly duplicateTargetOptions = computed(() => this.bases().filter((base) => base.id !== this.selectedBaseId()));
  readonly activeDocumentCount = computed(() => this.selectedDocuments().filter((document) => document.status === 'active').length);
  readonly activationProgress = computed(() => Math.min(100, (this.activeDocumentCount() / 5) * 100));
  readonly canCreateBase = computed(() => this.baseName().trim().length > 0 && Boolean(this.baseType()) && !this.accessBlocked());
  readonly canCreateSource = computed(() => {
    if (!this.selectedBase() || !this.sourceName().trim() || this.accessBlocked()) return false;
    if (this.sourceMode() === 'url') return this.sourceUrl().trim().length > 0;
    if (this.sourceMode() === 'file') return Boolean(this.sourceFile());
    if (this.sourceMode() === 'csv') return Boolean(this.sourceCsvFile());
    return this.sourceManualContent().trim().length > 0;
  });
  readonly canUpdateSource = computed(() => {
    const source = this.editingSource();
    if (!source || !this.updateSourceName().trim() || this.accessBlocked()) return false;
    if (source.sourceType === 'url') return this.updateSourceUrl().trim().length > 0;
    if (this.sourceUpdateMode() === 'file') return Boolean(this.updateFile());
    if (this.sourceUpdateMode() === 'csv') return Boolean(this.updateCsvFile());
    if (this.sourceUpdateMode() === 'manual') return this.updateManualContent().trim().length > 0;
    return true;
  });
  readonly canRetrieve = computed(() => {
    const selectedBase = this.selectedBase();
    return Boolean(selectedBase) && selectedBase?.status === 'active' && this.retrievalQuery().trim().length > 0 && !this.accessBlocked();
  });

  constructor() {
    this.headerSearch.register(
      {
        id: 'knowledge.documents.search',
        label: 'Buscar documentos',
        placeholder: 'Buscar documentos',
        value: this.documentSearch,
        setValue: (value) => {
          this.documentSearch.set(value);
          this.trimSelectedDocumentsToVisible();
        },
        clear: () => {
          this.documentSearch.set('');
          this.trimSelectedDocumentsToVisible();
        },
      },
      this.destroyRef,
    );

    this.reload();
  }

  reload() {
    this.loadingBases.set(true);
    this.accessBlocked.set(false);

    this.api
      .listBases()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.bases.set(response.items);
          this.loadingBases.set(false);
          this.loadLibraryCounts(response.items);

          const selectedBaseId = this.selectedBaseId();
          if (selectedBaseId && response.items.some((base) => base.id === selectedBaseId)) {
            this.loadSelectedResources(selectedBaseId);
          } else {
            this.selectedBaseId.set(null);
            this.clearDetail();
          }
        },
        error: (error: unknown) => {
          this.handleError(error);
          this.bases.set([]);
          this.clearDetail();
          this.loadingBases.set(false);
        },
      });
  }

  openBase(base: KnowledgeBase) {
    this.selectedBaseId.set(base.id);
    this.cancelSourceUpdate();
    this.retrievalResults.set([]);
    this.loadSelectedResources(base.id);
  }

  closeBase() {
    this.selectedBaseId.set(null);
    this.cancelSourceUpdate();
    this.clearDetail();
  }

  refreshCurrentView() {
    const selectedBaseId = this.selectedBaseId();
    if (selectedBaseId) {
      this.loadSelectedResources(selectedBaseId);
      return;
    }
    this.reload();
  }

  setLibraryView(mode: LibraryViewMode) {
    this.libraryView.set(mode);
  }

  setLibraryViewValue(value: unknown) {
    const mode = firstString(value);
    if (mode === 'grid' || mode === 'list') this.setLibraryView(mode);
  }

  toggleBaseCreation() {
    this.creatingBase.update((value) => !value);
  }

  openSourceDialog() {
    this.clearSourceForm();
    this.sourceMode.set('url');
    this.sourceDialogOpen.set(true);
  }

  closeSourceDialog() {
    if (this.savingSource()) return;
    this.sourceDialogOpen.set(false);
    this.clearSourceForm();
  }

  setSourceMode(mode: SourceComposerMode) {
    this.sourceMode.set(mode);
  }

  setSourceModeValue(value: unknown) {
    const mode = firstString(value);
    if (mode === 'url' || mode === 'file' || mode === 'manual' || mode === 'csv') this.setSourceMode(mode);
  }

  setSourceUpdateMode(mode: SourceUpdateMode) {
    this.sourceUpdateMode.set(mode);
  }

  setSourceUpdateModeValue(value: unknown) {
    const mode = firstString(value);
    if (mode === 'metadata' || mode === 'file' || mode === 'manual' || mode === 'csv') this.setSourceUpdateMode(mode);
  }

  setDocumentStatusFilter(value: unknown) {
    const status = firstString(value);
    if (status === 'all' || status === 'active' || status === 'inactive' || status === 'deleted') {
      this.documentStatusFilter.set(status);
      this.trimSelectedDocumentsToVisible();
    }
  }

  setDocumentSourceFilter(value: unknown) {
    const sourceId = firstString(value);
    if (!sourceId) return;
    this.documentSourceFilter.set(sourceId);
    this.trimSelectedDocumentsToVisible();
  }

  chooseBaseType(type: KnowledgeBaseType) {
    this.baseType.set(type);
  }

  setSignal(signalRef: WritableSignal<string>, event: Event) {
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      signalRef.set(target.value);
    }
  }

  setDocumentSearch(event: Event) {
    this.setSignal(this.documentSearch, event);
    this.trimSelectedDocumentsToVisible();
  }

  isDocumentSelected(documentId: string): boolean {
    return this.selectedDocumentSet().has(documentId);
  }

  setDocumentSelected(documentId: string, checked: boolean) {
    this.selectedDocumentIds.update((ids) => {
      if (checked) return ids.includes(documentId) ? ids : [...ids, documentId];
      return ids.filter((id) => id !== documentId);
    });
  }

  clearDocumentSelection() {
    this.selectedDocumentIds.set([]);
  }

  toggleDocumentActive(document: KnowledgeDocument) {
    this.documentMenuId.set(null);
    this.setDocumentsStatus([document.id], document.status === 'active' ? 'inactive' : 'active');
  }

  setDocumentActive(document: KnowledgeDocument, checked: boolean) {
    this.setDocumentsStatus([document.id], checked ? 'active' : 'inactive');
  }

  bulkActivateDocuments() {
    this.setDocumentsStatus(this.selectedDocumentIds(), 'active');
  }

  bulkDeactivateDocuments() {
    this.setDocumentsStatus(this.selectedDocumentIds(), 'inactive');
  }

  deleteSelectedDocuments() {
    this.deleteDocuments(this.selectedDocumentIds());
  }

  deleteDocument(document: KnowledgeDocument) {
    this.documentMenuId.set(null);
    this.deleteDocuments([document.id]);
  }

  toggleDocumentMenu(documentId: string) {
    this.documentMenuId.update((current) => current === documentId ? null : documentId);
  }

  closeDocumentMenu() {
    this.documentMenuId.set(null);
  }

  openDuplicateDialogForDocument(document: KnowledgeDocument) {
    this.documentMenuId.set(null);
    this.openDuplicateDialog([document.id]);
  }

  openDuplicateDialogForSelection() {
    this.openDuplicateDialog(this.selectedDocumentIds());
  }

  closeDuplicateDialog() {
    this.duplicateDialogDocumentIds.set([]);
    this.duplicateTargetBaseId.set('');
  }

  setDuplicateTargetBase(value: unknown) {
    const targetId = firstString(value);
    if (targetId) this.duplicateTargetBaseId.set(targetId);
  }

  duplicateDocuments() {
    const targetBaseId = this.duplicateTargetBaseId();
    const duplicateIds = this.duplicateDialogDocumentIds();
    if (!targetBaseId || duplicateIds.length === 0) return;

    if (targetBaseId === this.selectedBaseId()) {
      const selectedIds = new Set(duplicateIds);
      const copies = this.selectedDocuments()
        .filter((document) => selectedIds.has(document.id))
        .map((document) => ({
          ...document,
          id: `${document.id}-copy-${Date.now()}`,
          title: `${document.title} cópia`,
          createdAt: new Date().toISOString(),
        }));
      this.documents.update((items) => [...copies, ...items]);
    }

    this.closeDuplicateDialog();
    this.clearDocumentSelection();
  }

  exportSelectedDocumentsCsv() {
    const documents = this.selectedBulkDocuments();
    if (documents.length === 0) return;

    const rows = [
      ['id', 'title', 'status', 'source', 'content_text', 'created_at'],
      ...documents.map((document) => [
        document.id,
        document.title,
        document.status,
        this.sourceForDocument(document),
        document.contentText,
        document.createdAt,
      ]),
    ];
    const content = rows.map((row) => row.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = `documentos-${this.selectedBase()?.name ?? 'biblioteca'}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  setSourceFile(event: Event) {
    const file = readSingleFile(event);
    this.sourceFile.set(file);
    if (file && !this.sourceName().trim()) this.sourceName.set(stripExtension(file.name));
  }

  setSourceCsvFile(event: Event) {
    const file = readSingleFile(event);
    this.sourceCsvFile.set(file);
    if (file && !this.sourceName().trim()) this.sourceName.set(stripExtension(file.name));
  }

  setUpdateFile(event: Event) {
    this.updateFile.set(readSingleFile(event));
  }

  setUpdateCsvFile(event: Event) {
    this.updateCsvFile.set(readSingleFile(event));
  }

  createBase() {
    if (!this.canCreateBase()) return;

    const body: CreateKnowledgeBaseRequest = {
      name: this.baseName().trim(),
      description: this.baseDescription().trim() || null,
      type: this.baseType(),
    };

    this.savingBase.set(true);

    this.api
      .createBase(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.bases.update((items) => [response.knowledgeBase, ...items.filter((item) => item.id !== response.knowledgeBase.id)]);
          this.savingBase.set(false);
          this.creatingBase.set(false);
          this.clearBaseForm();
          this.openBase(response.knowledgeBase);
        },
        error: (error: unknown) => {
          this.handleError(error);
          this.savingBase.set(false);
        },
      });
  }

  async archiveBase(base: KnowledgeBase) {
    if (this.isArchivingBase(base.id) || this.accessBlocked()) return;

    const confirmed = await this.confirm.confirm({
      title: 'Excluir biblioteca?',
      description: `${base.name} será arquivada. Fontes e documentos continuam preservados, mas a biblioteca sai da recuperação dos agentes.`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.archivingBaseIds.update((ids) => [...ids, base.id]);
    this.api
      .archiveBase(base.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.archivingBaseIds.update((ids) => ids.filter((id) => id !== base.id));
          this.bases.update((items) => items.filter((item) => item.id !== response.knowledgeBase.id));
          if (this.selectedBaseId() === response.knowledgeBase.id) {
            this.closeBase();
          }
          this.toasts.success('Biblioteca excluída', 'A biblioteca foi arquivada e saiu da lista operacional.');
        },
        error: (error: unknown) => {
          this.archivingBaseIds.update((ids) => ids.filter((id) => id !== base.id));
          this.handleError(error);
        },
      });
  }

  isArchivingBase(baseId: string) {
    return this.archivingBaseIds().includes(baseId);
  }

  createSource() {
    const selectedBase = this.selectedBase();
    if (!selectedBase || !this.canCreateSource()) return;

    this.savingSource.set(true);

    this.buildCreateSourceRequest(selectedBase.id)
      .pipe(
        switchMap((body) => this.api.createSource(body)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.sources.update((items) => [response.source, ...items.filter((item) => item.id !== response.source.id)]);
          if (response.ingestionJobs?.length) {
            this.jobs.update((items) => [...response.ingestionJobs!, ...items]);
          }
          this.clearSourceForm();
          this.sourceDialogOpen.set(false);
          this.savingSource.set(false);
          this.loadSelectedResources(selectedBase.id);
        },
        error: (error: unknown) => {
          this.handleError(error);
          this.savingSource.set(false);
        },
      });
  }

  startSourceUpdate(source: KnowledgeSource) {
    this.updateContext.set('source');
    this.editingDocumentId.set(null);
    this.prepareSourceUpdate(source);
  }

  startDocumentEdit(document: KnowledgeDocument) {
    this.editingDocumentContentId.set(document.id);
    this.documentEditTitle.set(document.title);
    this.documentEditSections.set(this.documentSections(document).map((section) => ({
      key: section.key,
      label: section.label,
      kind: section.kind,
      value: section.kind === 'text' ? section.value : section.items.join('\n'),
    })));
  }

  closeDocumentEdit() {
    this.editingDocumentContentId.set(null);
    this.documentEditTitle.set('');
    this.documentEditSections.set([]);
  }

  setDocumentEditSectionValue(key: string, event: Event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    this.documentEditSections.update((sections) =>
      sections.map((section) => section.key === key ? { ...section, value: target.value } : section),
    );
  }

  saveDocumentEdit() {
    const document = this.editingDocumentContent();
    if (!document || !this.documentEditTitle().trim()) return;

    const sections = this.documentEditSections();
    const title = this.documentEditTitle().trim();
    const contentText = compileEditedDocumentText(title, sections);
    const contentData = applyEditedContentData(document, this.selectedBase()?.type ?? 'other', title, sections);

    this.documents.update((items) =>
      items.map((item) =>
        item.id === document.id
          ? {
              ...item,
              title,
              contentText,
              contentData,
            }
          : item,
      ),
    );
    this.closeDocumentEdit();
  }

  cancelSourceUpdate() {
    this.editingSourceId.set(null);
    this.editingDocumentId.set(null);
    this.updateContext.set('source');
    this.sourceUpdateMode.set('metadata');
    this.updateSourceName.set('');
    this.updateSourceUrl.set('');
    this.updateSourceTags.set('');
    this.updateManualContent.set('');
    this.updateFile.set(null);
    this.updateCsvFile.set(null);
  }

  toggleSourceDetails(sourceId: string) {
    this.expandedSourceId.update((current) => current === sourceId ? null : sourceId);
  }

  isSourceExpanded(sourceId: string): boolean {
    return this.expandedSourceId() === sourceId;
  }

  updateCurrentSource() {
    const source = this.editingSource();
    const selectedBaseId = this.selectedBaseId();
    if (!source || !selectedBaseId || !this.canUpdateSource()) return;

    this.updatingSource.set(true);

    this.buildUpdateSourceRequest(source)
      .pipe(
        switchMap((body) => this.api.updateSource(source.id, body)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.sources.update((items) => items.map((item) => (item.id === response.source.id ? response.source : item)));
          if (response.ingestionJobs?.length) {
            this.jobs.update((items) => [...response.ingestionJobs!, ...items]);
          }
          this.cancelSourceUpdate();
          this.updatingSource.set(false);
          this.loadSelectedResources(selectedBaseId);
        },
        error: (error: unknown) => {
          this.handleError(error);
          this.updatingSource.set(false);
        },
      });
  }

  reprocessSource(source: KnowledgeSource) {
    const selectedBaseId = this.selectedBaseId();
    if (!selectedBaseId || this.accessBlocked()) return;

    this.updatingSource.set(true);

    this.api
      .updateSource(source.id, { name: source.name, tags: source.tags })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.ingestionJobs?.length) {
            this.jobs.update((items) => [...response.ingestionJobs!, ...items]);
          }
          this.updatingSource.set(false);
          this.loadSelectedResources(selectedBaseId);
        },
        error: (error: unknown) => {
          this.handleError(error);
          this.updatingSource.set(false);
        },
      });
  }

  activateSelectedBase() {
    this.setSelectedBaseActive(true);
  }

  setSelectedBaseActive(checked: boolean) {
    const selectedBase = this.selectedBase();
    const status: KnowledgeBaseStatus = checked ? 'active' : 'draft';
    if (!selectedBase || selectedBase.status === status || this.accessBlocked()) return;
    if (checked && this.activeDocumentCount() < 5) return;

    this.api
      .updateBase(selectedBase.id, { status })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.bases.update((items) => items.map((item) => (item.id === response.knowledgeBase.id ? response.knowledgeBase : item)));
        },
        error: (error: unknown) => this.handleError(error),
      });
  }

  retrieve() {
    const businessId = this.businessId();
    const selectedBase = this.selectedBase();
    if (!businessId || !selectedBase || !this.canRetrieve()) return;

    this.retrieving.set(true);

    this.api
      .retrieve({
        businessId,
        query: this.retrievalQuery().trim(),
        allowedKnowledgeBaseIds: this.activeBases().map((base) => base.id),
        knowledgeBaseIds: [selectedBase.id],
        topK: 5,
        mode: 'hybrid',
        rerank: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.retrievalResults.set(response.results);
          this.retrieving.set(false);
        },
        error: (error: unknown) => {
          this.handleError(error);
          this.retrieving.set(false);
        },
      });
  }

  downloadCsvTemplate() {
    const baseType = this.selectedBase()?.type ?? this.baseType();
    const columns = CSV_TEMPLATES[baseType];
    const sample = columns.map((column) => `${column}_exemplo`);
    const content = `${columns.join(',')}\n${sample.join(',')}\n`;
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = `template-${baseType}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  typeLabel(type: KnowledgeBaseType | null | undefined): string {
    return type ? TYPE_LABELS[type] : 'Tipo não definido';
  }

  typeIcon(type: KnowledgeBaseType | null | undefined): string {
    return this.typeOptions.find((option) => option.value === type)?.icon ?? 'heroBookOpen';
  }

  statusLabel(status: KnowledgeBaseStatus | string): string {
    const labels: Record<string, string> = {
      active: 'Ativa',
      archived: 'Arquivada',
      cancelled: 'Cancelado',
      completed: 'Concluído',
      deleted: 'Excluída',
      draft: 'Rascunho',
      failed: 'Falhou',
      inactive: 'Inativo',
      partial: 'Parcial',
      pending: 'Pendente',
      processing: 'Processando',
    };
    return labels[status] ?? status;
  }

  sourceTypeLabel(source: KnowledgeSource): string {
    const labels: Record<string, string> = {
      upload: 'Arquivo',
      url: 'URL',
      integration_sync: 'Integração',
    };
    return labels[source.sourceType] ?? source.sourceType;
  }

  sourceIcon(source: KnowledgeSource): string {
    if (source.sourceType === 'url') return 'heroLink';
    if (source.sourceType === 'integration_sync') return 'heroQueueList';
    return 'heroCloudArrowUp';
  }

  sourceDetail(source: KnowledgeSource): string {
    if (source.sourceType === 'url') return source.sourceUrl ?? 'URL sem endereço registrado';
    if (source.sourceType === 'upload') return source.filename ?? source.fileBucketKey ?? 'Upload registrado';
    return source.externalObjectId ?? source.providerResourceTypeKey ?? 'Fonte de integração';
  }

  sourceForDocument(document: KnowledgeDocument): string {
    return this.sourceById().get(document.sourceId)?.name ?? 'Fonte não carregada';
  }

  documentPreview(document: KnowledgeDocument): string {
    const view = this.toDocumentView(document);
    return view.preview;
  }

  resultPreview(result: KnowledgeRetrievalResult): string {
    return truncate(result.contentText, 190);
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return 'Sem data';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  fileSize(bytes: number | null | undefined): string {
    if (!bytes) return 'Tamanho não registrado';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  selectedFileName(file: File | null): string {
    return file?.name ?? 'Nenhum arquivo selecionado';
  }

  baseStatusLabel(status: KnowledgeBaseStatus | string): string {
    const labels: Record<string, string> = {
      active: 'Publicada',
      archived: 'Arquivada',
      draft: 'Rascunho',
      inactive: 'Inativa',
    };
    return labels[status] ?? this.statusLabel(status);
  }

  documentStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      active: 'Gerado',
      archived: 'Arquivado',
      deleted: 'Excluído',
      draft: 'Rascunho',
      failed: 'Falhou',
      inactive: 'Inativo',
      pending: 'Pendente',
      processing: 'Processando',
    };
    return labels[status] ?? this.statusLabel(status);
  }

  jobsForSource(sourceId: string): readonly KnowledgeIngestionJob[] {
    return this.jobsBySourceId().get(sourceId) ?? [];
  }

  latestJobForSource(sourceId: string): KnowledgeIngestionJob | null {
    return this.jobsForSource(sourceId)[0] ?? null;
  }

  documentIsActive(document: KnowledgeDocument): boolean {
    return document.status === 'active';
  }

  libraryActiveDocumentCount(base: KnowledgeBase): number {
    return normalizeCount(base.activeDocumentCount);
  }

  librarySourceCount(base: KnowledgeBase): number {
    return normalizeCount(base.sourceCount);
  }

  baseStatusTone(status: KnowledgeBaseStatus | string): KnowledgeBadgeTone {
    if (status === 'active') return 'success';
    if (status === 'draft') return 'warning';
    if (status === 'archived') return 'danger';
    return 'neutral';
  }

  sourceProcessingState(source: KnowledgeSource): SourceProcessingState {
    const job = this.latestJobForSource(source.id);
    if (!job) return 'success';
    if (job.status === 'failed' || job.status === 'partial' || job.status === 'cancelled') return 'failed';
    if (job.status === 'processing') return 'processing';
    if (job.status === 'pending') return 'pending';
    return 'success';
  }

  sourceStateIcon(source: KnowledgeSource): string {
    const state = this.sourceProcessingState(source);
    if (state === 'failed') return 'heroXCircle';
    if (state === 'processing') return 'heroArrowPath';
    return this.sourceIcon(source);
  }

  private setDocumentsStatus(documentIds: readonly string[], status: KnowledgeDocument['status']) {
    if (documentIds.length === 0) return;
    const ids = new Set(documentIds);
    this.documents.update((items) =>
      items.map((document) => ids.has(document.id) ? { ...document, status } : document),
    );
    this.refreshSelectedBaseCounts();
  }

  private deleteDocuments(documentIds: readonly string[]) {
    if (documentIds.length === 0) return;
    const ids = new Set(documentIds);
    this.documents.update((items) => items.filter((document) => !ids.has(document.id)));
    this.selectedDocumentIds.update((selectedIds) => selectedIds.filter((id) => !ids.has(id)));
    this.refreshSelectedBaseCounts();
  }

  private openDuplicateDialog(documentIds: readonly string[]) {
    const ids = documentIds.filter(Boolean);
    if (ids.length === 0) return;
    this.duplicateDialogDocumentIds.set([...new Set(ids)]);
    this.duplicateTargetBaseId.set(this.bases().find((base) => base.id !== this.selectedBaseId())?.id ?? '');
  }

  private trimSelectedDocumentsToVisible() {
    const visibleIds = new Set(this.filteredDocumentViews().map((view) => view.document.id));
    this.selectedDocumentIds.update((ids) => ids.filter((id) => visibleIds.has(id)));
  }

  private loadSelectedResources(baseId: string) {
    this.loadingDetail.set(true);
    forkJoin({
      sources: this.api.listSources(baseId),
      documents: this.api.listDocuments(baseId),
      jobs: this.api.listIngestionJobs(baseId),
      activeDocumentCount: this.api.countActiveDocuments(baseId),
      sourceCount: this.api.countActiveSources(baseId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ sources, documents, jobs, activeDocumentCount, sourceCount }) => {
          this.sources.set(sources.items);
          this.documents.set(documents.items);
          this.jobs.set(jobs.items);
          this.refreshSelectedBaseCounts(baseId, {
            activeDocumentCount,
            sourceCount,
          });
          this.selectedDocumentIds.set([]);
          this.documentMenuId.set(null);
          this.loadingDetail.set(false);
        },
        error: (error: unknown) => {
          this.handleError(error);
          this.loadingDetail.set(false);
        },
      });
  }

  private loadLibraryCounts(bases: readonly KnowledgeBase[]) {
    if (bases.length === 0) return;

    forkJoin(
      bases.map((base) =>
        forkJoin({
          activeDocumentCount: this.api.countActiveDocuments(base.id),
          sourceCount: this.api.countActiveSources(base.id),
        }).pipe(
          map((counts) => ({ baseId: base.id, ...counts })),
          catchError(() =>
            of({
              baseId: base.id,
              activeDocumentCount: normalizeCount(base.activeDocumentCount),
              sourceCount: normalizeCount(base.sourceCount),
            }),
          ),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countsByBase) => {
        const counts = new Map(countsByBase.map((item) => [item.baseId, item]));
        this.bases.update((items) =>
          items.map((base) => {
            const count = counts.get(base.id);
            return count
              ? {
                  ...base,
                  activeDocumentCount: count.activeDocumentCount,
                  sourceCount: count.sourceCount,
                }
              : base;
          }),
        );
      });
  }

  private refreshSelectedBaseCounts(
    baseId: string = this.selectedBaseId() ?? '',
    fallbackCounts?: { activeDocumentCount?: number; sourceCount?: number },
  ) {
    if (!baseId) return;
    const activeDocumentCount = fallbackCounts?.activeDocumentCount ?? this.documents().filter((document) => document.status === 'active').length;
    const sourceCount = fallbackCounts?.sourceCount ?? this.sources().length;

    this.bases.update((items) =>
      items.map((base) =>
        base.id === baseId
          ? {
              ...base,
              activeDocumentCount,
              sourceCount,
            }
          : base,
      ),
    );
  }

  private buildCreateSourceRequest(knowledgeBaseId: string): Observable<CreateKnowledgeSourceRequest> {
    const base = {
      name: this.sourceName().trim(),
      tags: this.parseTags(this.sourceTags()),
      knowledgeBaseIds: [knowledgeBaseId],
    };

    if (this.sourceMode() === 'url') {
      return of({
        ...base,
        sourceType: 'url',
        sourceUrl: this.sourceUrl().trim(),
      });
    }

    if (this.sourceMode() === 'manual') {
      return this.fileUpload
        .uploadText(`${slugify(base.name)}.txt`, 'text/plain', this.sourceManualContent().trim())
        .pipe(map((file) => ({ ...base, sourceType: 'upload', fileId: file.fileId })));
    }

    const file = this.sourceMode() === 'csv' ? this.sourceCsvFile() : this.sourceFile();
    if (!file) return of({ ...base, sourceType: 'upload', fileId: '' });

    return this.fileUpload.upload(file).pipe(map((uploaded) => ({ ...base, sourceType: 'upload', fileId: uploaded.fileId })));
  }

  private buildUpdateSourceRequest(source: KnowledgeSource): Observable<UpdateKnowledgeSourceRequest> {
    const base: UpdateKnowledgeSourceRequest = {
      name: this.updateSourceName().trim(),
      tags: this.parseTags(this.updateSourceTags()),
    };

    if (source.sourceType === 'url') {
      return of({ ...base, sourceUrl: this.updateSourceUrl().trim() });
    }

    if (source.sourceType !== 'upload') {
      return of(base);
    }

    if (this.sourceUpdateMode() === 'manual') {
      return this.fileUpload
        .uploadText(`${slugify(this.updateSourceName())}.txt`, 'text/plain', this.updateManualContent().trim())
        .pipe(map((uploaded) => ({ ...base, fileId: uploaded.fileId })));
    }

    const file = this.sourceUpdateMode() === 'csv' ? this.updateCsvFile() : this.updateFile();
    if (file) {
      return this.fileUpload.upload(file).pipe(map((uploaded) => ({ ...base, fileId: uploaded.fileId })));
    }

    return of(base);
  }

  private prepareSourceUpdate(source: KnowledgeSource) {
    this.editingSourceId.set(source.id);
    this.sourceUpdateMode.set('metadata');
    this.updateSourceName.set(source.name);
    this.updateSourceUrl.set(source.sourceUrl ?? '');
    this.updateSourceTags.set(source.tags.join(', '));
    this.updateManualContent.set('');
    this.updateFile.set(null);
    this.updateCsvFile.set(null);
  }

  private clearDetail() {
    this.sources.set([]);
    this.documents.set([]);
    this.jobs.set([]);
    this.retrievalResults.set([]);
    this.selectedDocumentIds.set([]);
    this.documentSearch.set('');
    this.documentStatusFilter.set('all');
    this.documentSourceFilter.set('all');
    this.documentMenuId.set(null);
    this.closeDuplicateDialog();
  }

  private clearBaseForm() {
    this.baseName.set('');
    this.baseDescription.set('');
    this.baseType.set('faq');
  }

  private clearSourceForm() {
    this.sourceName.set('');
    this.sourceUrl.set('');
    this.sourceTags.set('');
    this.sourceManualContent.set('');
    this.sourceFile.set(null);
    this.sourceCsvFile.set(null);
  }

  private parseTags(value: string): string[] {
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  private handleError(error: unknown) {
    if (isForbiddenError(error)) {
      this.accessBlocked.set(true);
    }
    if (!(error instanceof HttpErrorResponse)) {
      this.toasts.error('Biblioteca', readErrorMessage(error));
    }
  }

  private toDocumentView(document: KnowledgeDocument): DocumentViewModel {
    const title = this.displayTitleForDocument(document);
    const sections = this.documentSections(document);
    const previewSource = (sections
      .map((section) => section.kind === 'list' ? section.items.join('; ') : section.value)
      .map((value) => stripRepeatedTitle(value, title))
      .find((value) => value.trim().length > 0)
      ?? stripRepeatedTitle(document.contentText, title))
      || title;

    return {
      document,
      title,
      preview: truncate(normalizeWhitespace(previewSource), 320),
      sourceName: this.sourceForDocument(document),
      sections,
    };
  }

  private displayTitleForDocument(document: KnowledgeDocument): string {
    const type = this.selectedBase()?.type ?? 'other';
    if (type !== 'faq') return document.title;

    const question = text(asRecord(document.contentData), ['question']);
    return question || document.title;
  }

  private documentSections(document: KnowledgeDocument): DocumentSection[] {
    const type = this.selectedBase()?.type ?? 'other';
    const content = asRecord(document.contentData);

    if (type === 'faq') {
      return compactSections([
        textSection('answer', 'Resposta', text(content, ['answer'], stripRepeatedTitle(document.contentText, document.title))),
        badgeSection('category', 'Categoria', [text(content, ['category'])].filter(Boolean)),
        badgeSection('relatedTopics', 'Tópicos relacionados', textArray(content, ['relatedTopics', 'related_topics'])),
      ]);
    }

    if (type === 'product_catalog') {
      return compactSections([
        textSection('description', 'Descrição', text(content, ['description'], stripRepeatedTitle(document.contentText, document.title))),
        badgeSection('category', 'Categoria', [text(content, ['category'])].filter(Boolean)),
        textSection('sku', 'SKU', text(content, ['sku'])),
        textSection('price', 'Preço', text(content, ['price'])),
        listSection('features', 'Recursos', textArray(content, ['features'])),
        metadataSection('specs', 'Especificações', recordEntries(content, ['specs'])),
      ]);
    }

    if (type === 'processes') {
      return compactSections([
        listSection('steps', 'Etapas', textArray(content, ['steps'])),
        listSection('prerequisites', 'Pré-requisitos', textArray(content, ['prerequisites'])),
        textSection('expectedOutcome', 'Resultado esperado', text(content, ['expectedOutcome', 'expected_outcome'])),
        listSection('exceptions', 'Exceções', textArray(content, ['exceptions'])),
      ]);
    }

    if (type === 'policies') {
      return compactSections([
        textSection('scope', 'Escopo', text(content, ['scope'])),
        listSection('rules', 'Regras', textArray(content, ['rules'])),
        textSection('effectiveDate', 'Vigência', text(content, ['effectiveDate', 'effective_date'])),
        listSection('exceptions', 'Exceções', textArray(content, ['exceptions'])),
      ]);
    }

    if (type === 'sales_scripts') {
      return compactSections([
        textSection('opening', 'Abertura', text(content, ['opening'])),
        listSection('discoveryQuestions', 'Perguntas de descoberta', textArray(content, ['discoveryQuestions', 'discovery_questions'])),
        listSection('objectionHandling', 'Objeções', textArray(content, ['objectionHandling', 'objection_handling'])),
        textSection('closing', 'Fechamento', text(content, ['closing'])),
      ]);
    }

    if (type === 'playbooks') {
      return compactSections([
        textSection('objective', 'Objetivo', text(content, ['objective'])),
        listSection('steps', 'Etapas', textArray(content, ['steps'])),
        listSection('successCriteria', 'Critérios de sucesso', textArray(content, ['successCriteria', 'success_criteria'])),
        listSection('tips', 'Dicas', textArray(content, ['tips'])),
      ]);
    }

    return compactSections([
      textSection('content', 'Conteúdo', text(content, ['content'], stripRepeatedTitle(document.contentText, document.title))),
      badgeSection('tags', 'Tags', textArray(content, ['tags'])),
      metadataSection('metadata', 'Metadados', recordEntries(content, ['metadata'])),
    ]);
  }
}

function firstString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return null;
}

function readSingleFile(event: Event): File | null {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return null;
  return target.files?.item(0) ?? null;
}

function stripExtension(filename: string): string {
  return filename.replace(/\.[^.]+$/, '');
}

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'fonte-manual';
}

function truncate(value: string, length: number): string {
  return value.length > length ? `${value.slice(0, length)}...` : value;
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function normalizeSearch(value: string): string {
  return normalizeWhitespace(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function stripRepeatedTitle(value: string, title: string): string {
  const normalizedValue = normalizeWhitespace(value);
  const normalizedTitle = normalizeWhitespace(title);
  if (!normalizedValue || !normalizedTitle) return normalizedValue;

  if (!normalizedValue.toLocaleLowerCase('pt-BR').startsWith(normalizedTitle.toLocaleLowerCase('pt-BR'))) {
    return normalizedValue;
  }

  return normalizedValue.slice(normalizedTitle.length).replace(/^[\s:.,;|-]+/, '').trim();
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function text(record: Record<string, unknown>, keys: readonly string[], fallback = ''): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return normalizeWhitespace(value);
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  }
  return normalizeWhitespace(fallback);
}

function textArray(record: Record<string, unknown>, keys: readonly string[]): string[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value
        .map((item) => typeof item === 'string' ? normalizeWhitespace(item) : '')
        .filter(Boolean);
    }
    if (typeof value === 'string' && value.trim()) {
      return value
        .split(/[;\n]/)
        .map((item) => normalizeWhitespace(item))
        .filter(Boolean);
    }
  }
  return [];
}

function recordEntries(record: Record<string, unknown>, keys: readonly string[]): string[] {
  for (const key of keys) {
    const value = asRecord(record[key]);
    const entries = Object.entries(value)
      .map(([entryKey, entryValue]) => {
        if (typeof entryValue === 'string' || typeof entryValue === 'number' || typeof entryValue === 'boolean') {
          return `${entryKey}: ${entryValue}`;
        }
        return '';
      })
      .filter(Boolean);
    if (entries.length > 0) return entries;
  }
  return [];
}

function textSection(key: string, label: string, value: string): DocumentSection {
  return { key, label, value, items: [], kind: 'text', display: 'text' };
}

function listSection(key: string, label: string, items: readonly string[]): DocumentSection {
  return { key, label, value: '', items, kind: 'list', display: 'list' };
}

function badgeSection(key: string, label: string, items: readonly string[]): DocumentSection {
  return { key, label, value: '', items, kind: 'list', display: 'badges' };
}

function metadataSection(key: string, label: string, items: readonly string[]): DocumentSection {
  return { key, label, value: '', items, kind: 'metadata', display: 'list' };
}

function compactSections(sections: readonly DocumentSection[]): DocumentSection[] {
  return sections.filter((section) => {
    if (section.kind === 'text') return section.value.trim().length > 0;
    return section.items.length > 0;
  });
}

function normalizeCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

function compileEditedDocumentText(title: string, sections: readonly EditableDocumentSection[]): string {
  const body = sections
    .map((section) => `${section.label}\n${section.value.trim()}`)
    .filter((value) => value.trim().length > 0)
    .join('\n\n');
  return [title, body].filter(Boolean).join('\n\n');
}

function applyEditedContentData(
  document: KnowledgeDocument,
  type: KnowledgeBaseType,
  title: string,
  sections: readonly EditableDocumentSection[],
): KnowledgeDocument['contentData'] {
  const current = asRecord(document.contentData);

  if (type === 'faq') {
    return {
      ...current,
      question: title,
      answer: sectionValue(sections, 'answer'),
      category: sectionNullableValue(sections, 'category'),
      relatedTopics: sectionLines(sections, 'relatedTopics'),
    } as KnowledgeDocument['contentData'];
  }

  if (type === 'product_catalog') {
    return {
      ...current,
      name: title,
      description: sectionValue(sections, 'description'),
      category: sectionNullableValue(sections, 'category'),
      sku: sectionNullableValue(sections, 'sku'),
      price: sectionNullableValue(sections, 'price'),
      features: sectionLines(sections, 'features'),
      specs: sectionEntries(sections, 'specs'),
    } as KnowledgeDocument['contentData'];
  }

  if (type === 'processes') {
    return {
      ...current,
      processName: title,
      steps: sectionLines(sections, 'steps'),
      prerequisites: sectionLines(sections, 'prerequisites'),
      expectedOutcome: sectionNullableValue(sections, 'expectedOutcome'),
      exceptions: sectionLines(sections, 'exceptions'),
    } as KnowledgeDocument['contentData'];
  }

  if (type === 'policies') {
    return {
      ...current,
      policyName: title,
      scope: sectionNullableValue(sections, 'scope'),
      rules: sectionLines(sections, 'rules'),
      effectiveDate: sectionNullableValue(sections, 'effectiveDate'),
      exceptions: sectionLines(sections, 'exceptions'),
    } as KnowledgeDocument['contentData'];
  }

  if (type === 'sales_scripts') {
    return {
      ...current,
      scenario: title,
      opening: sectionNullableValue(sections, 'opening'),
      discoveryQuestions: sectionLines(sections, 'discoveryQuestions'),
      objectionHandling: sectionLines(sections, 'objectionHandling'),
      closing: sectionNullableValue(sections, 'closing'),
    } as KnowledgeDocument['contentData'];
  }

  if (type === 'playbooks') {
    return {
      ...current,
      playbookName: title,
      objective: sectionValue(sections, 'objective'),
      steps: sectionLines(sections, 'steps'),
      successCriteria: sectionLines(sections, 'successCriteria'),
      tips: sectionLines(sections, 'tips'),
    } as KnowledgeDocument['contentData'];
  }

  return {
    ...current,
    title,
    content: sectionValue(sections, 'content'),
    tags: sectionLines(sections, 'tags'),
    metadata: sectionEntries(sections, 'metadata'),
  } as KnowledgeDocument['contentData'];
}

function sectionValue(sections: readonly EditableDocumentSection[], key: string): string {
  return normalizeWhitespace(sections.find((section) => section.key === key)?.value ?? '');
}

function sectionNullableValue(sections: readonly EditableDocumentSection[], key: string): string | null {
  const value = sectionValue(sections, key);
  return value || null;
}

function sectionLines(sections: readonly EditableDocumentSection[], key: string): string[] {
  const value = sections.find((section) => section.key === key)?.value ?? '';
  return value
    .split(/\n|;/)
    .map((item) => normalizeWhitespace(item))
    .filter(Boolean);
}

function sectionEntries(sections: readonly EditableDocumentSection[], key: string): Record<string, string> {
  return Object.fromEntries(
    sectionLines(sections, key)
      .map((line) => {
        const [entryKey, ...entryValue] = line.split(':');
        const normalizedKey = normalizeWhitespace(entryKey ?? '');
        const normalizedValue = normalizeWhitespace(entryValue.join(':'));
        return normalizedKey && normalizedValue ? [normalizedKey, normalizedValue] as const : null;
      })
      .filter((entry): entry is readonly [string, string] => Boolean(entry)),
  );
}

function isForbiddenError(error: unknown): boolean {
  const maybeError = error as { status?: number; statusText?: string; error?: unknown; message?: string };
  const nested = typeof maybeError.error === 'string' ? maybeError.error : JSON.stringify(maybeError.error ?? '');
  return maybeError.status === 403 || maybeError.statusText === 'Forbidden' || /forbidden/i.test(nested) || /forbidden/i.test(maybeError.message ?? '');
}

function readErrorMessage(error: unknown): string {
  if (isForbiddenError(error)) {
    return 'Você não tem permissão para carregar ou alterar a Biblioteca desta organização.';
  }

  const maybeError = error as { error?: { message?: string; error?: string } | string; message?: string };
  if (typeof maybeError.error === 'string') return maybeError.error;
  if (maybeError.error?.message) return maybeError.error.message;
  if (maybeError.error?.error) return maybeError.error.error;
  if (maybeError.message) return maybeError.message;
  return 'Não foi possível concluir a operação na Biblioteca.';
}
